import { IonButtons, IonContent, IonHeader, IonMenuButton, IonPage, IonTitle, IonToolbar, IonCardContent, IonCard, IonBackButton, IonCardHeader, IonCardTitle, IonCardSubtitle, IonButton, IonIcon, IonLabel } from '@ionic/react';
import React from 'react';
import ReactMarkdownWithHtml from 'react-markdown/with-html';
import YouTube from 'react-youtube';
import { useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { Content, ContentType } from '../features/content/content';
import { selectContent } from '../features/content/contentSlice';
import { selectSum } from '../features/log/logSlice';
import { bookOutline, bookSharp } from 'ionicons/icons';

interface VideoViewProps { // type for `match.params`
  contentId: string; // must be type `string` since value comes from the URL
}

interface TopicDetailProps extends RouteComponentProps<VideoViewProps> {
    // any other props (leave empty if none)
}

const processTextContent : (content : Content, text : string) => string = (content, text) => {
  const photoRe = /{(PHOTO|photo) (\d+) \| ([^}]*)}/g;
  const urls = (text.match(photoRe) || [])
    .map(match => {
      const photoNumber = match.split(photoRe)[2],
        index = Number(photoNumber) - 1,
        url = (content.attachments || [])[index];
      return url;
    })

  // insert image URLs
  let iterativeText = text.replace(photoRe, `![](#$2#)\n\n##### $3`);
  (iterativeText.match(/\(#(\d+)#\)/g) || []).forEach(match =>
    iterativeText = iterativeText.replace(
      match,
      `(${urls[
        Number(
          match.replace('(#', '').replace('#)', '')
        ) - 1
      ]})`
    )
  );

  // Youtube videos

  // footnotes — not active links
  iterativeText = iterativeText.replace(/\[\^(\d+)\]/g, `<a id="footnote-$1-link"></a><a href="${window.location.origin}${window.location.pathname}#footnote-$1">\\[$1\\]</a>`);
  iterativeText = iterativeText.replace(/\[\^(\d+)=\]/g, `<a id="footnote-$1"></a><a href="${window.location.origin}${window.location.pathname}#footnote-$1-link">\\[$1\\]</a>`);

  return iterativeText;
}

const videoIdFromYoutubeURL = (url : string) => {
  const match = url.match(/(?:https?:\/{2})?(?:w{3}\.)?youtu(?:be)?\.(?:com|be)(?:\/watch\?v=|\/)([^\s&]+)/);
  return match && match.length > 0 ? match[1] : undefined;
}


const VideoView : React.FC<TopicDetailProps> = ({ match }) => {
  const allContent = useSelector(selectContent),
    content : Content | undefined = (allContent.content.data || []).find(c => c.id === match?.params?.contentId),
    sum = useSelector(selectSum),
    // unlock all content
    isUnlocked = true ;//|| (content && content?.unlocked_after <= sum);

  if(content) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton />
              <IonMenuButton />
            </IonButtons>
            <IonTitle>{content.title}</IonTitle>
          </IonToolbar>
        </IonHeader>
  
        <IonContent fullscreen className="text-content">
          <IonHeader collapse="condense">
            <IonToolbar>
              <IonTitle size="large">{content.title}</IonTitle>
            </IonToolbar>
          </IonHeader>
          {isUnlocked && <IonCard>
            <IonCardHeader>
              <IonCardTitle>{content.title}</IonCardTitle>
              {content.author && <IonCardSubtitle>{content.author}</IonCardSubtitle>}
              {content.scripture && <IonButton
                  href={`https://www.biblegateway.com/passage/?search=${content.scripture}&version=NRSV`}
                  fill="clear"
                  target="_blank"
                >
                  <IonIcon slot="start" ios={bookOutline} md={bookSharp}/>
                  <IonLabel>{content.scripture}</IonLabel>
                </IonButton>}
            </IonCardHeader>
            <IonCardContent>

              {/* Video: attachment or Youtube embed */}
              {content.type === ContentType.Video && content.url && <video key={content.url} controls>
                <source src={content.url} type="video/mp4"/>
              </video>}
              {content.type === ContentType.Video && !content.url && content.youtube_url && <YouTube videoId={videoIdFromYoutubeURL(content.youtube_url)}></YouTube>}

              {/* Photo */}
              {content.type === ContentType.Photo && content.url && <img src={content.url} alt={content.content ? content.content : content.title}/>}

              {/* Anything with text content (including videos or photos) */}
              {content.content && <div><ReactMarkdownWithHtml source={processTextContent(content, content.content)} allowDangerousHtml={true} /></div>}
            </IonCardContent>
          </IonCard>}
          {!isUnlocked && <IonCard>
            <IonCardHeader>
              <IonCardTitle>You’re not there yet!</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              It looks like you’re trying to access a video you haven’t unlocked yet.
            </IonCardContent>
          </IonCard>}
        </IonContent>
      </IonPage>
    );
  } else {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonMenuButton />
            </IonButtons>
            <IonTitle>Video Not Found</IonTitle>
          </IonToolbar>
        </IonHeader>
  
        <IonContent fullscreen>
          <IonHeader collapse="condense">
            <IonToolbar>
              <IonTitle size="large">Video Not Found</IonTitle>
            </IonToolbar>
          </IonHeader>
          <IonCard>
            <IonCardContent>
              <p>Sorry—we seem to have trouble finding that video!</p>
            </IonCardContent>
          </IonCard>
        </IonContent>
      </IonPage>
    );
  }
};

export default VideoView;